import React from 'react';
import { Component } from '../../../dispatcher/modules';
import {
  Notification,
  NotificationGroup
} from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import _ from 'lodash';
import uuid from 'uuid-random';
import classnames from 'classnames';
import { Position } from '../../../dispatcher/Types';
import { Translatable } from '../../../index';
import NotificationDetails from './details/NotificationDetails';
import { 
  ACTION_CONFIG, 
  NOTIFICATION_TEXT_PARAMS,
  NOTIFICATION,
  TOAST_NOTIFICATIONS_STATE,
  TOAST_NOTIFICATIONS_PROPS
} from './Types';

const NOTIFICATION_GROUP_POSITION = {
  left: 'left',
  middle: 'middle',
  right: 'right'
};

const NOTIFICATION_GROUP_POSITION_STYLES = {
  left: {
    zIndex: 14000,
    bottom: '20px',
    left: '16px'
  },
  middle: {
    zIndex: 14000,
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)'
  },
  right: {
    zIndex: 14000,
    bottom: '20px',
    right: '16px'
  }
};

const NOTIFICATION_STYLE_MAP = {
  none: 'none',
  dark: 'none',
  success: 'success',
  warning: 'warning',
  info: 'info'
};

export default class ToastNotifications extends Component<TOAST_NOTIFICATIONS_PROPS, TOAST_NOTIFICATIONS_STATE> {
  state: TOAST_NOTIFICATIONS_STATE = {
    notifications: {
      left: [],
      middle: [],
      right: []
    },
    extendedNotificationMap: {},
    isNotificationHovered: false
  };

  /**
   * Add's new notification to notification list
   * @param {string} id
   * @param {NOTIFICATION_TYPE} type
   * @param {NOTIFICATION_TEXT_PARAMS} notificationTextParams
   * @param {ACTION_CONFIG} actionConfig
   * @param {string} linkWord
   * @param {NOTIFICATION_POSITION} position
   */
  addNotification = (notification: NOTIFICATION): void => {
    const newNotification = {
      ...notification,
      id: _.get(notification, 'id', uuid()),
      withIcon: _.get(notification, 'withIcon', true),
      isPermanent: _.get(notification, 'isPermanent', false),
      isClosable: _.get(notification, 'isClosable', true),
      position: _.get(notification, 'position', this.props.defaultPosition || 'right'),
      size: _.get(notification, 'size', 'normal'),
      icon: _.get(notification, 'icon', 'reload')
    };

    const { notifications } = this.state;

    const isExistingId = notifications[newNotification.position].some((notif: NOTIFICATION) => notif.id === newNotification.id);

    let newStateValues;
    if (isExistingId) {
      newStateValues = notifications[newNotification.position].map((notif: NOTIFICATION) => {
        if (notif.id === newNotification.id) {
          return newNotification;
        }
        return notif;
      });
    } else {
      newStateValues = [
        newNotification,
        ...notifications[newNotification.position]
      ];
    }

    this.setState({
      notifications: {
        ...notifications,
        [newNotification.position]: [
          ...newStateValues
        ]
      },
      extendedNotificationMap: {
        ...this.state.extendedNotificationMap,
        [newNotification.id]: _.get(newNotification.details, 'initialExpand', true)
      }
    }, () => {
      if (!newNotification.isPermanent) {
        this.debounceNotificationRemoval(newNotification);
      }
    });
  };

  /**
   * Debounce function for removing/prolonging notification visibility
   * @param notification
   * @param duration
   */
  debounceNotificationRemoval = (notification: NOTIFICATION, duration: number = 6000): void => {
    setTimeout(() => {
      if (!this.state.isNotificationHovered) { 
        this.removeNotificationFromList(notification); 
      } else { 
        this.debounceNotificationRemoval(notification, 1000);
      }
    }, duration);
  }

  /**
   * Removes notification form list
   * @param notification
   */
  removeNotificationFromList = (notification: NOTIFICATION): void => {
    const newNotifications = { ...this.state.notifications };
    newNotifications[notification.position] = this.state.notifications[notification.position]
      .filter((item: NOTIFICATION) => item.id !== notification.id);
    this.setState({ notifications: newNotifications });
  };

  /**
   * Handles click events on link click
   * Passes params as event and action config where data prepares for filtering
   * @param {any} e
   * @param {ACTION_CONFIG} actionConfig
   */
  onClickHandler(e: any, actionConfig: ACTION_CONFIG): void {
    e.preventDefault();
    let { name = 'executeToastNotificationsLink', actionType = Position.end, data } = actionConfig;

    if (data.filterDataMap) {
      const { dataItem, filterDataMap } = data;
      const filterData = {};

      if (filterDataMap) {
        filterDataMap.forEach((item: any) => {
          const { filterFieldName, valueFieldName } = item;
          filterData[filterFieldName] = {
            values: [dataItem[valueFieldName]]
          };
        });
        data.filterData = { ...filterData };
      }
    } else {
      name = 'executeFilterRequest';
    }

    data.refreshedFilterId = uuid();
    this.dispatch(name, actionType, data);
  }

  /**
   * Wraps part of the string with link
   * @param text
   * @param linkWord
   * @param actionConfig
   * */
  textWrapper(notificationTextParams: NOTIFICATION_TEXT_PARAMS, linkWord: string, actionConfig: ACTION_CONFIG) {
    const phrasesToWrap = [linkWord];

    const translatedText: string | React.ReactElement =
      this.translate(notificationTextParams.locale, notificationTextParams.params, notificationTextParams.moduleName);
    let remainingText = typeof translatedText === 'string'
      ? translatedText
      : _.get(translatedText, 'props.dangerouslySetInnerHTML.__html', '');

    const textParts = [];

    // Loop through the phrases and wrap them with links
    phrasesToWrap.forEach((phrase) => {
      const index = remainingText.indexOf(phrase);

      if (index !== -1) {
        // Add the text before the matched phrase
        textParts.push(remainingText.substring(0, index));

        // Add the matched phrase wrapped in a link
        if (!!linkWord) {
          textParts.push(
            <a
              key={phrase}
              className={'tp-text-semibold tp-pointable tp-text-underline'}
              onClick={e => this.onClickHandler(e, actionConfig)}
            >
              {phrase}
            </a>
          );
        } else {
          textParts.push(phrase);
        }
        // Update the remaining text
        remainingText = remainingText.substring(index + phrase.length);
      }
    });
    // Add any remaining text
    if (remainingText) {
      textParts.push(remainingText);
    }
    return textParts.map((textPart: any, index: number) =>
      typeof textPart === 'string'
        ? Translatable.dangerouslySetInnerHTML(`part${index}`, textPart)
        : textPart
    );
  }

  /**
   * On mouse over handler for setting notification hover status
   */
  onMouseOver = () => {
    const { isNotificationHovered } = this.state;
    if (!isNotificationHovered) {
      this.setState({
        isNotificationHovered: true
      });
    }
  }

  /**
   * On mouse leave handler for setting notification hover status
   */
  onMouseLeave = () => {
    const { isNotificationHovered } = this.state;
    if (isNotificationHovered) {
      this.setState({
        isNotificationHovered: false
      });
    }
  }

  /**
   * Returns notification list
   */
  renderNotifications = (position: string): JSX.Element[] => {
    const { notifications, extendedNotificationMap } = this.state;

    return notifications[position]
      .map(((notification: NOTIFICATION) => {
        const {
          id,
          notificationTextParams,
          type,
          actionConfig,
          linkWord,
          withIcon,
          details,
          isClosable,
          size,
          icon
        } = notification;

        return (
          <div
            className={classnames(`tp-notification-container ${type}`, {
              'tp-notification-small': size === 'small'
            })}
            key={id}
          >
            <Notification
              className={`tp-notification-${type} tp-notification-icon-${icon}`}
              style={{ pointerEvents: 'all' }}
              type={{ style: NOTIFICATION_STYLE_MAP[type], icon: withIcon }}
              closable={isClosable}
              onClose={() => this.removeNotificationFromList(notification)}
            >
              <span
                className={!!linkWord ? '' : 'tp-text-semibold'}
                role={'alert'}
              >
                {this.textWrapper(notificationTextParams, linkWord, actionConfig)}
              </span>
              {details && <span
                className={`tp-icon ${extendedNotificationMap[id] ? 'tp-arrow-chevron-down' : 'tp-arrow-chevron-up'}`}
                onClick={() => {
                  this.setState({
                    extendedNotificationMap: {
                      ...extendedNotificationMap,
                      [id]: !extendedNotificationMap[id]
                    }
                  });
                }}
              />}
            </Notification>
            {extendedNotificationMap[id] && details && <NotificationDetails
              notification={notification}
            />}
          </div>
        );
      }));
  };

  render() {
    return (
      <span onMouseOver={this.onMouseOver} onMouseLeave={this.onMouseLeave}>
        <NotificationGroup
          className='tp-notification-group'
          style={NOTIFICATION_GROUP_POSITION_STYLES.left}
        >
          <Fade enter={true} exit={true}>
            {this.renderNotifications(NOTIFICATION_GROUP_POSITION.left)}
          </Fade>
        </NotificationGroup>
        <NotificationGroup
          className='tp-notification-group'
          style={NOTIFICATION_GROUP_POSITION_STYLES.middle}
        >
          <Fade enter={true} exit={true}>
            {this.renderNotifications(NOTIFICATION_GROUP_POSITION.middle)}
          </Fade>
        </NotificationGroup>
        <NotificationGroup
          className='tp-notification-group'
          style={NOTIFICATION_GROUP_POSITION_STYLES.right}
        >
          <Fade enter={true} exit={true}>
            {this.renderNotifications(NOTIFICATION_GROUP_POSITION.right)}
          </Fade>
        </NotificationGroup>
      </span>
    );
  }
}

